import React from "react";
import { Label } from "~/components/ui/label";
import { FieldComponentProps, isFieldApi } from "~/lib/form";
import { cn } from "~/shared/lib/utils";
import { t } from "@lingui/core/macro";

interface ValidatedColorPickerProps extends FieldComponentProps {
  description?: string;
}

export const ValidatedColorPicker: React.FC<ValidatedColorPickerProps> = (props) => {
  const { field, label, className, description } = props;
  const inputId = `${field.name}-${React.useId()}-color-picker`;

  if (!isFieldApi(field)) {
    return null;
  }

  const meta = field.state.meta;
  const rawValue = typeof field.state.value === "string" ? field.state.value : "";
  const normalizedValue = rawValue.trim();
  const hasValue = normalizedValue.length > 0;
  const hexPattern = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/;
  const isValidHex = hasValue && hexPattern.test(normalizedValue);
  const colorValue = isValidHex ? normalizedValue : "#000000";
  const hexError = hasValue && !isValidHex ? "Enter a valid hex color." : null;
  const showFieldErrors = meta.isTouched && meta.errors.length > 0;
  const describedBy =
    [
      showFieldErrors ? `${inputId}-errors` : null,
      hexError ? `${inputId}-hex-error` : null,
    ]
      .filter(Boolean)
      .join(" ") || undefined;

  // Preset colors for quick selection
  const presetColors = [
    { value: "#E2E2E2", label: t`Light gray` },
    { value: "#ff75c3", label: t`Pink` },
    { value: "#ffa647", label: t`Orange` },
    { value: "#ffe83f", label: t`Yellow` },
    { value: "#9fff5b", label: t`Light green` },
    { value: "#70e2ff", label: t`Light blue` },
    { value: "#cd93ff", label: t`Purple` },
    { value: "#09203f", label: t`Dark blue` },
  ];

  return (
    <div className={cn("space-y-2", className)}>
      <Label htmlFor={inputId}>{label}</Label>
      {description && <p className="text-muted-foreground text-sm">{description}</p>}
      <div className="flex flex-col gap-3">
        {/* Color input with visual preview */}
        <div className="flex items-center gap-2">
          <input
            type="color"
            id={inputId}
            name={field.name}
            value={colorValue}
            onChange={(e) => field.handleChange(e.target.value)}
            onBlur={field.handleBlur}
            className="border-input h-10 w-20 cursor-pointer rounded border"
            aria-invalid={showFieldErrors || !!hexError}
            aria-describedby={describedBy}
          />
          <input
            type="text"
            value={rawValue}
            onChange={(e) => {
              const nextValue = e.target.value;
              field.handleChange(nextValue.trim() === "" ? null : nextValue);
            }}
            onBlur={field.handleBlur}
            placeholder={t`#000000`}
            className={cn(
              "border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-32 rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
              (showFieldErrors || hexError) && "border-destructive",
            )}
            aria-invalid={showFieldErrors || !!hexError}
            aria-describedby={describedBy}
            disabled={field.form.state.isSubmitting}
          />
          <span className="text-muted-foreground text-sm">
            {hasValue ? normalizedValue : "Inherit theme default"}
          </span>
        </div>
        {hexError && (
          <p
            id={`${inputId}-hex-error`}
            role="alert"
            aria-live="polite"
            className="text-destructive text-sm"
          >
            {hexError}
          </p>
        )}

        {/* Preset color palette */}
        <div className="flex flex-wrap gap-1">
          {presetColors.map((color) => (
            <button
              key={color.value}
              type="button"
              className={cn(
                "border-border h-6 w-6 rounded-md border shadow-sm transition-transform hover:scale-110",
                field.state.value === color.value && "ring-ring ring-2 ring-offset-1",
              )}
              style={{ backgroundColor: color.value }}
              onClick={() => field.handleChange(color.value)}
              aria-label={`Select ${color.label} (${color.value})`}
            />
          ))}
        </div>
      </div>
      {showFieldErrors && (
        <div
          id={`${inputId}-errors`}
          role="alert"
          aria-live="polite"
          className="text-destructive text-sm font-medium"
        >
          {label}: {meta.errors.join(", ")}
        </div>
      )}
    </div>
  );
};
